import React, { Component } from "react";
import { connect } from "react-redux";
import { ButtonWithIcon } from "./ButtonWithIcon";
import { Loader, TextInput } from "@mantine/core";
import {
	fetchPublicShareDetails,
	getSignedUrlForZipDownload,
	validateSharePassword,
} from "../actions/shareActions.js";

import ScrollableListOfFiles from "./ScrollableListOfFiles";
import { IconDownload, IconEye, IconEyeOff } from "@tabler/icons-react";

const shareNotAvailable = () => (
	<div>
		<h2 style={{ textAlign: "center", marginTop: 100 }}>
			This link is invalid or has expired.
		</h2>
	</div>
);

class PublicShareDetails extends Component {
	constructor(props) {
		super(props);
		this.state = { enteredPassword: "", showPassword: false };
		this.downloadFiles = this.downloadFiles.bind(this);
		this.handlePasswordSubmit = this.handlePasswordSubmit.bind(this);
	}

	downloadFiles() {
		this.props.getSignedUrlForZipDownload(
			this.props.shareDetails.accessHash,
			this.props.shareDetails.userRegion || "US",
			this.state.enteredPassword || null
		);
	}

	handlePasswordSubmit() {
		this.props.validateSharePassword(
			this.props.shareDetails.accessHash,
			this.props.shareDetails.userRegion || "US",
			this.state.enteredPassword
		);
	}

	renderFooter() {
		if (!this.props.showFooter) return null;
		return (
			<div
				style={{ position: "absolute", bottom: 20 }}
				className="ShareFooter"
			>
				Powered by{" "}
				<a href="https://lexworkplace.com">LexWorkplace&trade;</a>
			</div>
		);
	}

	renderMessage() {
		const { shareDetails } = this.props;
		if (!shareDetails.message) return null;
		return (
			<>
				<div style={{ marginTop: 20, marginBottom: 6 }}>
					Message from {shareDetails.senderName}:
				</div>
				<div
					className="GreyContainer"
					style={{
						maxHeight: 200,
						width: "100%",
						overflowY: "scroll",
						whiteSpace: "pre",
					}}
				>
					<div>{shareDetails.message}</div>
				</div>
			</>
		);
	}

	renderPasswordGate() {
		const { shareDetails, passwordError } = this.props;
		return (
			<div>
				<h2>{shareDetails.firmName}</h2>
				<div style={{ marginTop: 16, color: "#444" }}>
					{shareDetails.senderName} has shared documents with you.
				</div>

				{this.props.shareDetails.passwordProtected ? (
					<>
						<div
							style={{
								marginTop: 20,
								padding: "8px 12px",
								backgroundColor: "#fff8e1",
								border: "1px solid #f9a825",
								borderRadius: 4,
							}}
						>
							This share is protected by a password. Contact{" "}
							{this.props.shareDetails.firmName} to obtain the
							password.
							<div
								style={{
									color: "#666666",
								}}
							>
								Share ID: {this.props.shareDetails.shareID}
							</div>
						</div>
					</>
				) : null}
				{this.renderMessage()}
				<div
					style={{
						paddingTop: 24,
					}}
				>
					<TextInput
						placeholder="Share password"
						label="Enter the share password to view the files:"
						type={this.state.showPassword ? "text" : "password"}
						style={{ width: 300, marginBottom: 25 }}
						value={this.state.enteredPassword}
						onChange={(e) =>
							this.setState({ enteredPassword: e.target.value })
						}
						error={passwordError ? "Incorrect password" : null}
						rightSection={
							<span
								onClick={() =>
									this.setState((s) => ({
										showPassword: !s.showPassword,
									}))
								}
								style={{ cursor: "pointer", display: "flex" }}
							>
								{this.state.showPassword ? (
									<IconEyeOff size={16} />
								) : (
									<IconEye size={16} />
								)}
							</span>
						}
					/>
					<ButtonWithIcon
						buttonText="View Share Details"
						className="blue"
						handler={this.handlePasswordSubmit}
						disabled={!this.state.enteredPassword}
					/>
				</div>
				{this.renderFooter()}
			</div>
		);
	}

	renderShareDetails() {
		const { shareDetails } = this.props;
		return (
			<div>
				<h2>{shareDetails.firmName}</h2>
				<div style={{ marginTop: 25, marginBottom: 10 }}>
					{shareDetails.senderName} has shared the following documents
					with you:
				</div>
				<ScrollableListOfFiles
					documentList={shareDetails.documentList}
				/>
				<div style={{ color: "#666666", marginTop: 10 }}>
					{shareDetails.documentCount} documents total
				</div>
				{this.renderMessage()}
				<div style={{ marginTop: 25 }}>
					<ButtonWithIcon
						buttonText="Download Files"
						iconComponent={
							<IconDownload
								size={16}
								style={{ verticalAlign: "middle" }}
							/>
						}
						className="blue"
						handler={this.downloadFiles}
					/>
				</div>
				{this.renderFooter()}
			</div>
		);
	}

	render() {
		const { shareDetails, loading, shareAvailable } = this.props;
		const needsPassword =
			shareDetails.passwordProtected && !shareDetails.documentList;

		return (
			<div
				style={{
					marginLeft: "auto",
					marginRight: "auto",
					width: "50%",
				}}
			>
				{loading ? (
					<div>
						<h1 style={{ marginTop: 200 }}>
							<center>
								<Loader />
							</center>
						</h1>
					</div>
				) : !shareAvailable ? (
					shareNotAvailable()
				) : needsPassword ? (
					this.renderPasswordGate()
				) : (
					this.renderShareDetails()
				)}
			</div>
		);
	}
}

class PublicShareDetailsStandaloneUnconnected extends PublicShareDetails {
	componentDidMount() {
		this.props.fetchPublicShareDetails(
			this.props.match.params.accessHash,
			this.props.match.params.userRegion || "US"
		);
	}
}

/*
	This is used on the "share detail" page, to show
	the public share information in the same format as
	what is shown on the public, unauthenticated page.

	Note that this appears on the /sharing/outgoing/:shareID(\d+)
	page (only visible to a user at the firm who sent the share)

	This is different than the PUBLIC "download share" page

*/
const PublicSharePageBase = connect(
	(state) => {
		return {
			shareDetails: state.share.publicShareDetails,
			loading: state.share.loading,
			shareAvailable: state.share.shareAvailable,
			passwordError: state.share.passwordError,
			showFooter: false,
		};
	},
	{ getSignedUrlForZipDownload, validateSharePassword }
)(PublicShareDetails);

/*
	This is the unauthenticated public page designed to be seen
	by the share recipient.
*/
const PublicSharePageStandalone = connect(
	(state) => {
		return {
			shareDetails: state.share.publicShareDetails,
			loading: state.share.loading,
			shareAvailable: state.share.shareAvailable,
			passwordError: state.share.passwordError,
			showFooter: true,
		};
	},
	{
		fetchPublicShareDetails,
		getSignedUrlForZipDownload,
		validateSharePassword,
	}
)(PublicShareDetailsStandaloneUnconnected);

/*
	This is used so that an authenticated LexWorkplace user can view
	the /share/download/ page.  The viewer could be:
		A) A user at the same firm who sent the share
		B) A user at a different firm, in the same region
		C) A user at a different firm, in a different region

	In all cases, the user will still see their regular
	authenticated LexWorkplace header/sidebar/footer
*/
const PublicSharePageStandaloneForAuthenticatedUser = connect(
	(state) => {
		return {
			shareDetails: state.share.publicShareDetails,
			loading: state.share.loading,
			shareAvailable: state.share.shareAvailable,
			passwordError: state.share.passwordError,
			showFooter: false,
		};
	},
	{
		fetchPublicShareDetails,
		getSignedUrlForZipDownload,
		validateSharePassword,
	}
)(PublicShareDetailsStandaloneUnconnected);

export {
	PublicSharePageStandalone,
	PublicSharePageBase,
	PublicSharePageStandaloneForAuthenticatedUser,
};
